import {Box, Typography} from '@mui/material';
import Cookies from 'js-cookie';
import React, {Fragment, useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';

import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {
	AbandonFlowModal,
	AlertActions,
	AlertModal,
	FailToCompleteActionModal,
	StepProgressBar,
} from 'presentation/components';
import {CustomStepper} from 'presentation/components/CustomStepper';
import {MainHeader} from 'presentation/components/MainHeader';
import {PageWrapperLayout} from 'presentation/components/PageWrapperLayout';
import {FeaturesEnum, HomeEnum, ProfileEnum, RefundEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {useGetBeneficiaryBankDataQuery} from 'presentation/pages/private/profile/queries/useGetBeneficiaryBankDataQuery';
import {useValidateRefundBankQuery} from 'presentation/pages/private/profile/queries/useValidateRefundBankQuery';
import {BankValidityEnum} from 'domain/usecases/private/refund/request/get-validate-bank';
import {translations} from 'presentation/translations-new/';

import {BeneficiaryData} from '../../components';
import {ConclusionModal} from '../../components/ConclusionModal';
import {RefundVariantEnum} from '../../constants/refundVariantEnum';
import {useBackButtonExitModal, useRefundRequest} from '../../hooks';
import {useResetRefundFlow} from '../../hooks/useResetRefundFlow';
import {ReceiptRegistration, ServiceType} from '../Request/components';
import {ConferenceTherapies} from '../Request/components/ConferenceTherapies';
import {Therapies} from '../Request/components/Therapies';
import {useTherapies} from '../Request/components/Therapies/hooks/useTherapies';
import {useExitFlowModalStore} from '../Request/store/useExitFlowModalStore';
import {useExitFlowSidebarRouteStore} from '../Request/store/useExitFlowSidebarRouteStore';
import {useStepStore} from '../Request/store/useStepStore';
import {useSubmitStatusStore} from '../Request/store/useSubmitStatusStore';
import {StyledStack} from '../Request/styles';
import {StepEnum, SubmitStatusEnum} from '../Request/types';
import {Conference} from './components/Conference';
import {SendDocuments} from './components/SendDocuments';
import {useFinishRefundV2} from './hooks/useFinishRefundV2';
import {usePendingRefundUploadsStore} from './store/usePendingRefundUploadsStore';
import {RequestContentPropsV2} from './types';
import type {RefundFlowNavigationState} from '../../types/refundFlowNavigationState';

const {requestRefundTitle, steps, stepsTitle, discardModal} =
	translations['pt-br'].privateFlow.refund;

const {
	description: errorDescription,
	headerTitle: errorHeaderTitle,
	title: errorTitle,
} = translations['pt-br'].privateFlow.refund.conferencePage.errorModal;

const {invalidBankDataModal} = translations['pt-br'].privateFlow.refund.holderDataPage.modals;

const {tryAgain, goToHome} = translations['pt-br'].commons;

// Mapa display order → enum v1: nova ordem dos passos com Therapies condicional na pos 5.
const V2_DISPLAY_ORDER_BASE: StepEnum[] = [
	StepEnum.BENEFICIARY_DATA,
	StepEnum.SERVICE_TYPE,
	StepEnum.SEND_DOCUMENTS,
	StepEnum.RECEIPT_REGISTRATION,
	StepEnum.REVIEW,
];

const V2_DISPLAY_ORDER_THERAPIES: StepEnum[] = [
	StepEnum.BENEFICIARY_DATA,
	StepEnum.SERVICE_TYPE,
	StepEnum.SEND_DOCUMENTS,
	StepEnum.RECEIPT_REGISTRATION,
	StepEnum.THERAPIES,
	StepEnum.REVIEW,
];

const RequestContentV2 = ({step}: RequestContentPropsV2) => {
	const navigate = useNavigate();
	const {state} = useLocation();
	const refundTermsReturnTo = (state as RefundFlowNavigationState | null)?.refundTermsReturnTo;
	const {isTablet} = useMediaQueries();
	const {resetRefundFlow} = useResetRefundFlow();
	const scrollToTopRef = document.getElementById('content-container');
	const {requests, isTherapiesFlowEnabled} = useRefundRequest();
	const {isAddOrEditPageMode, openModalEditDataAndCleanProcedures} = useTherapies();
	const {isEnabled: isRefundBankAndAgencyEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_REFUND_BANK_AND_AGENCY,
	);
	const {data: bankData} = useGetBeneficiaryBankDataQuery();
	const {data: validateBankData} = useValidateRefundBankQuery({
		bankCode: bankData?.bank?.code ?? '',
		agencyCode: bankData?.agency ?? '',
		enabled: isRefundBankAndAgencyEnabled,
	});

	const hideMainHeader = isTherapiesFlowEnabled && isAddOrEditPageMode;

	const resetSteps = useStepStore(s => s.resetStep);
	const setStep = useStepStore(s => s.setStep);
	const exitFlowModal = useExitFlowModalStore(s => s.open);
	const setExitFlowModal = useExitFlowModalStore(s => s.setOpen);
	const resetExitFlowModal = useExitFlowModalStore(s => s.reset);
	const setSubmitStatus = useSubmitStatusStore(s => s.setStatus);
	const exitFlowSidebarRouteState = useExitFlowSidebarRouteStore(s => s.route);
	const resetExitFlowSidebarRouteState = useExitFlowSidebarRouteStore(s => s.reset);
	const resetPendingFiles = usePendingRefundUploadsStore(s => s.resetPendingFiles);

	const [bankAccountModalState, setBankAccountModalState] = useState(false);
	const [conclusionModalState, setConclusionModalState] = useState<boolean>(false);

	const {finish, resetState, IS_LOADING, IS_ERROR, IS_DONE} = useFinishRefundV2();

	const displayOrder = isTherapiesFlowEnabled
		? V2_DISPLAY_ORDER_THERAPIES
		: V2_DISPLAY_ORDER_BASE;

	const stepsLabelsBase = [
		steps.holderData,
		steps.service,
		steps.sendDocuments,
		steps.receiptRegistration,
		steps.review,
	];

	const stepsLabelsWhenTherapies = [
		steps.holderData,
		steps.service,
		steps.sendDocuments,
		steps.receiptRegistration,
		steps.procedures,
		steps.review,
	];

	const stepsLabels = isTherapiesFlowEnabled ? stepsLabelsWhenTherapies : stepsLabelsBase;

	const titleByStep: Partial<Record<StepEnum, string | undefined>> = {
		[StepEnum.BENEFICIARY_DATA]: stepsTitle.holderData,
		[StepEnum.SERVICE_TYPE]: stepsTitle.service,
		[StepEnum.SEND_DOCUMENTS]: stepsTitle.sendDocuments,
		[StepEnum.RECEIPT_REGISTRATION]: stepsTitle.receiptRegistration,
		[StepEnum.THERAPIES]: isTablet ? stepsTitle.procedures : undefined,
		[StepEnum.REVIEW]: stepsTitle.review,
	};

	const title = titleByStep[step];

	const activeStepIndex = Math.max(displayOrder.indexOf(step), 0);

	const shouldOpenAbandonFlowModal =
		step === StepEnum.SEND_DOCUMENTS ||
		step === StepEnum.RECEIPT_REGISTRATION ||
		step === StepEnum.THERAPIES ||
		step === StepEnum.REVIEW;

	const handleGoToNextStep = async () => {
		if (
			isRefundBankAndAgencyEnabled &&
			validateBankData?.isValid === BankValidityEnum.INVALID
		) {
			setBankAccountModalState(true);
			return;
		}

		if (step === StepEnum.REVIEW) {
			await finish();
			return;
		}

		const currentIndex = displayOrder.indexOf(step);
		const nextEnum = displayOrder[currentIndex + 1];
		if (nextEnum !== undefined) setStep(nextEnum);
	};

	const handleBack = async () => {
		if (step === StepEnum.BENEFICIARY_DATA) {
			navigate(RefundEnum.TERMS, {
				state: refundTermsReturnTo ? {refundTermsReturnTo} : undefined,
			});
			return;
		}

		const lateSteps: StepEnum[] = [
			StepEnum.RECEIPT_REGISTRATION,
			StepEnum.THERAPIES,
			StepEnum.REVIEW,
		];
		if (lateSteps.includes(step)) {
			setSubmitStatus(SubmitStatusEnum.UPDATE_REQUEST);
		}

		if (isTherapiesFlowEnabled && step === StepEnum.THERAPIES) {
			openModalEditDataAndCleanProcedures();
		}

		const currentIndex = displayOrder.indexOf(step);
		const prevEnum = displayOrder[currentIndex - 1];
		if (prevEnum !== undefined) setStep(prevEnum);
	};

	const handleClose = () => {
		setExitFlowModal(true);
		Cookies.remove('uploadFilesTerm');
	};

	const handleAbandonFlowModalAction = () => {
		resetSteps();
		resetPendingFiles();
		resetExitFlowModal();
		if (exitFlowSidebarRouteState) {
			navigate(exitFlowSidebarRouteState);
			resetExitFlowSidebarRouteState();
			return;
		}
		if (refundTermsReturnTo) {
			navigate(refundTermsReturnTo);
			return;
		}
		navigate(RefundEnum.REFUND);
	};

	const handleGoToHome = () => {
		navigate(HomeEnum.HOME_PAGE);
	};

	const pagesByStep: Partial<Record<StepEnum, React.ReactNode>> = {
		[StepEnum.BENEFICIARY_DATA]: <BeneficiaryData handleNextStep={handleGoToNextStep} />,
		[StepEnum.SERVICE_TYPE]: <ServiceType handleNextStep={handleGoToNextStep} />,
		[StepEnum.SEND_DOCUMENTS]: <SendDocuments handleNextStep={handleGoToNextStep} />,
		[StepEnum.RECEIPT_REGISTRATION]: (
			<ReceiptRegistration
				nextStepWhenNoTherapies={StepEnum.REVIEW}
			/>
		),
		[StepEnum.THERAPIES]: <Therapies handleNextStep={handleGoToNextStep} />,
		[StepEnum.REVIEW]: isTherapiesFlowEnabled ? (
			<ConferenceTherapies handleNextStep={handleGoToNextStep} isLoading={IS_LOADING} />
		) : (
			<Conference handleNextStep={handleGoToNextStep} isLoading={IS_LOADING} />
		),
	};

	useBackButtonExitModal({
		setExitModal: setExitFlowModal,
		shouldOpenModal: shouldOpenAbandonFlowModal,
	});

	useEffect(() => {
		resetRefundFlow();
		resetExitFlowModal();
		resetPendingFiles();
	}, []);

	useEffect(() => {
		if (scrollToTopRef) {
			scrollToTopRef.scrollTo({
				top: 0,
				behavior: 'smooth',
			});
		}
	}, [step]);

	useEffect(() => {
		if (IS_DONE) {
			setConclusionModalState(true);
			Cookies.remove('uploadFilesTerm');
		}
	}, [IS_DONE]);

	return (
		<Fragment>
			<PageWrapperLayout title={requestRefundTitle}>
				{!hideMainHeader && (
					<MainHeader
						title={requestRefundTitle}
						handleBack={handleBack}
						variant="subRoute"
						handleClose={handleClose}
						dataTagId={{
							closeButton: RefundTaggingEnum.CLICK_FECHAR_SOLICITACAO_REEMBOLSO,
							backButton: RefundTaggingEnum.CLICK_VOLTAR_STEP_SOLICITACAO_REEMBOLSO,
						}}>
						<StyledStack gap={isTablet ? 2 : 3}>
							<Box pb={1}>
								{!isTablet ? (
									<CustomStepper
										activeStep={activeStepIndex}
										stepLabels={stepsLabels}
									/>
								) : (
									<Box px={3}>
										<StepProgressBar
											currentStep={activeStepIndex + 1}
											totalSteps={stepsLabels.length}
										/>
									</Box>
								)}
							</Box>
							{title ? (
								<Typography paddingLeft={1} variant="h6" mb={1}>
									{title}
								</Typography>
							) : null}
						</StyledStack>
					</MainHeader>
				)}
				{pagesByStep[step]}
			</PageWrapperLayout>

			<AlertModal
				open={bankAccountModalState}
				onClose={() => setBankAccountModalState(false)}
				headerTitle={invalidBankDataModal.headerTitle}
				variant="info"
				customIconName="info-filled"
				title={invalidBankDataModal.title}
				description={invalidBankDataModal.description}
				footerContent={
					<AlertActions
						primaryAction={() => {
							setBankAccountModalState(false);
							navigate(ProfileEnum.PROFILE);
						}}
						secondaryAction={() => setBankAccountModalState(false)}
						primaryText={invalidBankDataModal.primaryAction}
						secondaryText={invalidBankDataModal.secondaryAction}
						flexDirection="column-reverse"
					/>
				}
			/>

			<ConclusionModal
				modalDataTagId={RefundTaggingEnum.VISUALIZOU_MODAL_CONCLUSAO_SOLICITACAO}
				btnDataTagId={RefundTaggingEnum.CLICK_VOLTAR_PARA_TELA_INICIAL_SOLICITACAO}
				variant={RefundVariantEnum.DEFAULT}
				protocol={requests.protocol}
				open={conclusionModalState}
				setOpen={setConclusionModalState}
			/>

			<AbandonFlowModal
				closeButtondataTagId={RefundTaggingEnum.CLICK_FECHAR_ICONE_MODAL_ABANDONO_REEMBOLSO}
				backButtondataTagId={RefundTaggingEnum.CLICK_VOLTAR_MODAL_ABANDONO_REEMBOLSO}
				modaldataTagId={RefundTaggingEnum.VISUALIZOU_MODAL_ABANDONO_REEMBOLSO}
				confirmButtondataTagId={
					RefundTaggingEnum.CLICK_CONFIRMAR_BOTAO_MODAL_ABANDONO_REEMBOLSO
				}
				open={exitFlowModal}
				setOpen={setExitFlowModal}
				handleConfirm={handleAbandonFlowModalAction}
				title={discardModal.title}
				headerTitle={discardModal.headerTitle}
				description={discardModal.description}
			/>

			<FailToCompleteActionModal
				dataTagId={RefundTaggingEnum.VISUALIZOU_MODAL_DE_ERRO_AO_ENVIAR}
				headerTitle={errorHeaderTitle}
				description={errorDescription}
				title={errorTitle}
				open={IS_ERROR}
				setOpen={resetState}
				primaryActionText={tryAgain}
				secondaryActionText={goToHome}
				fallbackAction={handleGoToHome}
				handleTryAgain={handleGoToNextStep}
			/>
		</Fragment>
	);
};

export const RequestV2 = () => {
	const step = useStepStore(state => state.step);

	return <RequestContentV2 step={step} />;
};
